import type { MoveAccountRequest, MoveAccountResponse } from "@credit-acceptance/step6-bff-client";
import { useMutation } from "@tanstack/react-query";
import { t } from "i18next";

import { workbenchExitApi } from "../../../api/client";
import { useForceExitStore } from "../../../stores/forceExitStore";

export interface MoveAccountInput {
  approvalId: string;
  targetStatus: string;
  targetPhase: string;
  currentStatus: string;
  currentPhase: string;
}

/**
 * Falls back to a timestamp+random key when crypto.randomUUID is unavailable
 * (older browsers, non-secure contexts) so double-submit dedup never silently
 * goes missing.
 * @returns {string} A unique key suitable for the Idempotency-Key header.
 */
function generateIdempotencyKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Mutation hook backing the Exit modal's confirm (both Save & Exit and plain
 * Exit). Persists the analyst's chosen next status/phase via
 * `WorkbenchExitApi.moveAccount` (POST), which forwards to recap-service and
 * runs the full exit flow: status/phase transition, work-queue closure,
 * vehicle-sold-in-CAPS flip, lender notifications, car-lot attention flag, and
 * lock release. Self-guards against force-exit the same way useSaveWorkbench
 * does: reads the live store value (not a subscribed hook value) so a
 * force-exit flag set moments earlier is honored even before this component
 * re-renders. A per-attempt `Idempotency-Key` dedups accidental double-submits.
 * @returns {ReturnType<typeof useMutation>} The TanStack Mutation result.
 */
export function useMoveAccount() {
  return useMutation({
    mutationFn: async ({
      approvalId,
      targetStatus,
      targetPhase,
      currentStatus,
      currentPhase,
    }: MoveAccountInput): Promise<MoveAccountResponse> => {
      if (useForceExitStore.getState().shouldForceExit) {
        throw new Error(t("workbench.saveAbortedForceExit"));
      }

      const idempotencyKey = generateIdempotencyKey();
      const request: MoveAccountRequest = { targetStatus, targetPhase, currentStatus, currentPhase };
      const response = await workbenchExitApi.moveAccount(approvalId, request, idempotencyKey);
      return response.data;
    },
  });
}
